import {
  GlobalWorkerOptions,
  getDocument,
  type PDFDocumentLoadingTask,
  type PDFDocumentProxy,
} from 'pdfjs-dist';
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import {
  PdfPagePreview,
  type PdfPagePreviewItem,
  type PdfThumbnailRenderer,
} from './components';

GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

interface PdfInputPreviewProps {
  readonly fileId: string;
  readonly file: File;
  readonly selectedPages: readonly number[];
  readonly disabled?: boolean;
  readonly onSelectionChange: (pages: readonly number[]) => void;
  readonly onPageCount: (fileId: string, pageCount: number) => void;
}

export function PdfInputPreview({
  fileId,
  file,
  selectedPages,
  disabled = false,
  onSelectionChange,
  onPageCount,
}: PdfInputPreviewProps) {
  const loadingTaskRef = useRef<PDFDocumentLoadingTask | null>(null);
  const [document, setDocument] = useState<PDFDocumentProxy>();
  const [pages, setPages] = useState<readonly PdfPagePreviewItem[]>([]);

  useEffect(() => {
    let active = true;
    let loadedDocument: PDFDocumentProxy | undefined;
    const taskPromise = file.arrayBuffer().then((buffer) => {
      if (!active) return undefined;
      const task = getDocument({
        data: new Uint8Array(buffer),
        useSystemFonts: false,
      });
      loadingTaskRef.current = task;
      return task.promise;
    });

    void taskPromise.then(async (nextDocument) => {
      if (!active || nextDocument === undefined) {
        await loadingTaskRef.current?.destroy();
        return;
      }
      loadedDocument = nextDocument;
      const nextPages: PdfPagePreviewItem[] = [];
      for (let pageNumber = 1; pageNumber <= nextDocument.numPages; pageNumber += 1) {
        const page = await nextDocument.getPage(pageNumber);
        const viewport = page.getViewport({ scale: 1 });
        nextPages.push({
          id: `${fileId}:${pageNumber}`,
          pageNumber,
          width: viewport.width,
          height: viewport.height,
          rotation: page.rotate,
        });
        page.cleanup();
      }
      if (!active) return;
      setDocument(nextDocument);
      setPages(nextPages);
      onPageCount(fileId, nextDocument.numPages);
    }).catch(() => {
      // Encrypted and malformed PDFs may still be repairable by the robust worker.
      // The picker already performed signature validation, so preview failure is non-fatal.
    });

    return () => {
      active = false;
      setDocument(undefined);
      setPages([]);
      const task = loadingTaskRef.current;
      loadingTaskRef.current = null;
      if (task !== null) void task.destroy();
      else loadedDocument?.cleanup();
    };
  }, [file, fileId, onPageCount]);

  const selectedPageIds = useMemo(
    () => selectedPages.map((pageNumber) => `${fileId}:${pageNumber}`),
    [fileId, selectedPages],
  );

  const renderThumbnail = useCallback<PdfThumbnailRenderer>(async ({
    page,
    canvas,
    targetWidth,
    pixelRatio,
    signal,
  }) => {
    if (document === undefined || signal.aborted) return;
    const pdfPage = await document.getPage(page.pageNumber);
    try {
      const initial = pdfPage.getViewport({ scale: 1 });
      const scale = Math.max(0.1, (targetWidth * pixelRatio) / initial.width);
      const viewport = pdfPage.getViewport({ scale });
      canvas.width = Math.max(1, Math.ceil(viewport.width));
      canvas.height = Math.max(1, Math.ceil(viewport.height));
      canvas.style.inlineSize = `${Math.max(1, Math.ceil(viewport.width / pixelRatio))}px`;
      canvas.style.blockSize = `${Math.max(1, Math.ceil(viewport.height / pixelRatio))}px`;
      const renderTask = pdfPage.render({ canvas, viewport, background: '#ffffff' });
      const abort = () => renderTask.cancel();
      signal.addEventListener('abort', abort, { once: true });
      try {
        await renderTask.promise;
      } finally {
        signal.removeEventListener('abort', abort);
      }
    } finally {
      pdfPage.cleanup();
    }
  }, [document]);

  if (pages.length === 0 || document === undefined) return null;

  return (
    <PdfPagePreview
      pages={pages}
      selectedPageIds={selectedPageIds}
      renderThumbnail={renderThumbnail}
      disabled={disabled}
      onTogglePage={(pageId, selected) => {
        const pageNumber = Number(pageId.slice(pageId.lastIndexOf(':') + 1));
        const next = new Set(selectedPages);
        if (selected) next.add(pageNumber);
        else next.delete(pageNumber);
        onSelectionChange([...next].sort((left, right) => left - right));
      }}
      onSelectAll={() => onSelectionChange(pages.map(({ pageNumber }) => pageNumber))}
      onSelectNone={() => onSelectionChange([])}
    />
  );
}
